import React, { useState, useEffect, useMemo } from 'react';
import { Search, RefreshCw, Package, Calendar, MapPin, FileText } from 'lucide-react';
import { ordersService, OrderWithLinesDto, OrderLineDto } from '../../../../services/ordersService';
import { BackButton } from '../../../common/BackButton';

interface OrderLinesProps {
  onBack?: () => void;
}

const OrderLines: React.FC<OrderLinesProps> = ({ onBack }) => {
  const [orders, setOrders] = useState<OrderWithLinesDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [columnSearches, setColumnSearches] = useState<{[key: string]: string}>({
    orderNo: '',
    orderNoERP: '',
    customerNo: '',
    customerName: '',
    lineNo: '',
    itemNo: '',
    description: '',
    warehouse: '',
    quantity: ''
  });

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ordersService.getNewOrdersWithLines();
      setOrders(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Feil ved lasting av ordre');
      console.error('Error loading orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleColumnSearchChange = (column: string, value: string) => {
    setColumnSearches(prev => ({
      ...prev,
      [column]: value
    }));
  };

  // Flatten all order lines with their order info
  const allOrderLines = useMemo(() => {
    const lines: Array<{ order: OrderWithLinesDto['header'], line: OrderLineDto }> = [];
    orders.forEach(orderWithLines => {
      orderWithLines.lines.forEach(line => {
        lines.push({
          order: orderWithLines.header,
          line: line
        });
      });
    });
    return lines;
  }, [orders]);

  // Function to check if a line matches search criteria
  const lineMatchesSearch = (item: { order: OrderWithLinesDto['header'], line: OrderLineDto }): boolean => {
    const { order, line } = item;

    // Global search
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesOrder = 
        order.orderNo.toString().includes(searchLower) ||
        order.orderNoERP.toLowerCase().includes(searchLower) ||
        order.customerNo.toLowerCase().includes(searchLower) ||
        (order.customerName || '').toLowerCase().includes(searchLower);
      
      const matchesLine = 
        line.lineNo.toString().includes(searchLower) ||
        line.itemNo.toLowerCase().includes(searchLower) ||
        (line.description1 || '').toLowerCase().includes(searchLower);
      
      if (!matchesOrder && !matchesLine) return false;
    }

    // Column searches
    if (columnSearches.orderNo && !order.orderNo.toString().includes(columnSearches.orderNo)) return false;
    if (columnSearches.orderNoERP && !order.orderNoERP.toLowerCase().includes(columnSearches.orderNoERP.toLowerCase())) return false;
    if (columnSearches.customerNo && !order.customerNo.toLowerCase().includes(columnSearches.customerNo.toLowerCase())) return false;
    if (columnSearches.customerName && !(order.customerName || '').toLowerCase().includes(columnSearches.customerName.toLowerCase())) return false;
    if (columnSearches.lineNo && !line.lineNo.toString().includes(columnSearches.lineNo)) return false;
    if (columnSearches.itemNo && !line.itemNo.toLowerCase().includes(columnSearches.itemNo.toLowerCase())) return false;
    if (columnSearches.description && !(line.description1 || '').toLowerCase().includes(columnSearches.description.toLowerCase())) return false;
    if (columnSearches.warehouse && !(line.warehouse || '').toLowerCase().includes(columnSearches.warehouse.toLowerCase())) return false;
    if (columnSearches.quantity && line.quantity && !line.quantity.toString().includes(columnSearches.quantity)) return false;

    return true;
  };

  const filteredOrderLines = useMemo(() => {
    return allOrderLines.filter(lineMatchesSearch);
  }, [allOrderLines, searchTerm, columnSearches]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <RefreshCw className="animate-spin" size={20} />
          <span>Laster ordrelinjer...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <h3 className="text-red-800 font-medium">Feil ved lasting av ordrelinjer</h3>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button
          onClick={loadOrders}
          className="mt-3 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 flex items-center space-x-2"
        >
          <RefreshCw size={16} />
          <span>Prøv igjen</span>
        </button>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="mb-6 px-6 pt-6">
        <div className="flex items-center space-x-4 mb-4">
          <BackButton onClick={onBack || (() => {})} />
          <h1 className="text-2xl font-bold text-gray-800 flex items-center space-x-2">
            <FileText size={24} />
            <span>Ordrelinjer</span>
          </h1>
        </div>
        <p className="text-gray-600 mt-1">
          Detaljert visning av alle ordrelinjer - {filteredOrderLines.length} av {allOrderLines.length} linjer vises
        </p>
      </div>

      {/* Search and Controls */}
      <div className="bg-white rounded-lg shadow-sm border mb-4 p-4 mx-6">
        <div className="flex flex-col lg:flex-row lg:items-center space-y-3 lg:space-y-0 lg:space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Søk i alle felt..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={loadOrders}
              disabled={loading}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              <span>Oppdater</span>
            </button>
          </div>
        </div>
      </div>

      {/* Order Lines Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden mx-6 mb-6">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="space-y-2">
                    <span>Ordrenr</span>
                    <input
                      type="text"
                      placeholder="Søk..."
                      value={columnSearches.orderNo}
                      onChange={(e) => handleColumnSearchChange('orderNo', e.target.value)}
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="space-y-2">
                    <span>ERP Ordrenr</span>
                    <input
                      type="text"
                      placeholder="Søk..."
                      value={columnSearches.orderNoERP}
                      onChange={(e) => handleColumnSearchChange('orderNoERP', e.target.value)}
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="space-y-2">
                    <span>Kundenr</span>
                    <input
                      type="text"
                      placeholder="Søk..."
                      value={columnSearches.customerNo}
                      onChange={(e) => handleColumnSearchChange('customerNo', e.target.value)}
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="space-y-2">
                    <span>Kundenavn</span>
                    <input
                      type="text"
                      placeholder="Søk..."
                      value={columnSearches.customerName}
                      onChange={(e) => handleColumnSearchChange('customerName', e.target.value)}
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Leveringsdato
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="space-y-2">
                    <span>Linjenr</span>
                    <input
                      type="text"
                      placeholder="Søk..."
                      value={columnSearches.lineNo}
                      onChange={(e) => handleColumnSearchChange('lineNo', e.target.value)}
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="space-y-2">
                    <span>Varenr</span>
                    <input
                      type="text"
                      placeholder="Søk..."
                      value={columnSearches.itemNo}
                      onChange={(e) => handleColumnSearchChange('itemNo', e.target.value)}
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="space-y-2">
                    <span>Beskrivelse</span>
                    <input
                      type="text"
                      placeholder="Søk..."
                      value={columnSearches.description}
                      onChange={(e) => handleColumnSearchChange('description', e.target.value)}
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="space-y-2">
                    <span>Antall</span>
                    <input
                      type="text"
                      placeholder="Søk..."
                      value={columnSearches.quantity}
                      onChange={(e) => handleColumnSearchChange('quantity', e.target.value)}
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Enhet
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pris
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="space-y-2">
                    <span>Lager</span>
                    <input
                      type="text"
                      placeholder="Søk..."
                      value={columnSearches.warehouse}
                      onChange={(e) => handleColumnSearchChange('warehouse', e.target.value)}
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lokasjon
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Linje Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredOrderLines.map((item, index) => {
                const { order, line } = item;
                
                return (
                  <tr key={`${order.orderNo}-${line.lineNo}-${index}`} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{order.orderNo}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{order.orderNoERP}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{order.customerNo}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{order.customerName || '-'}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 flex items-center space-x-1">
                        <Calendar size={14} className="text-gray-400" />
                        <span>{ordersService.formatDate(order.deliveryDate)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{line.lineNo}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 font-mono">{line.itemNo}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{line.description1 || '-'}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 font-medium">
                        {ordersService.formatQuantity(line.quantity)}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{line.itemUnitCode || '-'}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {ordersService.formatCurrency(line.price)}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 flex items-center space-x-1">
                        <MapPin size={14} className="text-gray-400" />
                        <span>{line.warehouse || '-'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{line.stdLocationName || '-'}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {ordersService.getOrderLineStatusText(line.lineStatus)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredOrderLines.length === 0 && (
          <div className="text-center py-12">
            <Package size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">Ingen ordrelinjer funnet</h3>
            <p className="text-gray-500">
              {allOrderLines.length === 0 
                ? 'Ingen ordrelinjer tilgjengelig.' 
                : 'Ingen ordrelinjer matcher søkekriteriene.'
              }
            </p>
            {(searchTerm || Object.values(columnSearches).some(search => search !== '')) && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setColumnSearches({
                    orderNo: '',
                    orderNoERP: '',
                    customerNo: '',
                    customerName: '',
                    lineNo: '',
                    itemNo: '',
                    description: '',
                    warehouse: '',
                    quantity: ''
                  });
                }}
                className="mt-3 text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                Fjern alle filtre
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderLines;
